import React, { useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

const EVENT_TYPES = ['CHECK_IN', 'CHECK_OUT', 'BE_IN', 'BE_OUT']

export function UsageEvents() {
  const { t } = useContent()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [eventType, setEventType] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    const params = {}
    if (eventType !== 'all') params.eventType = eventType
    api
      .get('/api/usageevent', { params })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data.content || []))
      .catch((err) => setError(err.response?.data?.message || t('usageEvents.loadFailed')))
      .finally(() => setLoading(false))
  }, [eventType])

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const s = search.toLowerCase()
    return rows.filter(
      (e) =>
        (e.accountId || '').toLowerCase().includes(s) ||
        (e.eventLocationIdentifier || '').toLowerCase().includes(s) ||
        (e.agreementId || '').toLowerCase().includes(s) ||
        (e.transactionId || '').toLowerCase().includes(s) ||
        (e.eventSourceSupplierRics || '').toLowerCase().includes(s)
    )
  }, [rows, search])

  return (
    <Layout crumb={t('usageEvents.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h">{t('usageEvents.title')}</h1>
              <div className="page-head__sub">
                <T k="usageEvents.sub" />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    filtered,
                    [
                      { label: t('usageEvents.csv.timestamp'), get: (e) => e.eventTimeStamp || '' },
                      { key: 'accountId', label: t('usageEvents.csv.account') },
                      { key: 'eventType', label: t('usageEvents.csv.event') },
                      { key: 'eventLocationIdentifier', label: t('usageEvents.csv.location') },
                      { key: 'eventSourceSupplierName', label: t('usageEvents.csv.supplier') },
                      { key: 'eventSourceSupplierRics', label: t('usageEvents.csv.supplierRics') },
                      { key: 'agreementId', label: t('usageEvents.csv.agreement') },
                      { key: 'transactionId', label: t('usageEvents.csv.transaction') },
                    ],
                    'mas-usage-events'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('usageEvents.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{ marginBottom: 14 }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="tab-bar">
            <button
              className={'tab-bar__tab ' + (eventType === 'all' ? 'is-active' : '')}
              onClick={() => setEventType('all')}
            >
              {t('usageEvents.tab.all')} <span className="count">{rows.length}</span>
            </button>
            {EVENT_TYPES.map((type) => (
              <button
                key={type}
                className={'tab-bar__tab ' + (eventType === type ? 'is-active' : '')}
                onClick={() => setEventType(type)}
              >
                {type.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('usageEvents.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {t('usageEvents.count', { shown: filtered.length, total: rows.length })}
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('usageEvents.column.timestamp')}</th>
                  <th>{t('usageEvents.column.account')}</th>
                  <th>{t('usageEvents.column.event')}</th>
                  <th>{t('usageEvents.column.location')}</th>
                  <th>{t('usageEvents.column.supplier')}</th>
                  <th>{t('usageEvents.column.agreement')}</th>
                  <th>{t('usageEvents.column.transaction')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="usageEvents.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="usageEvents.empty" />
                    </td>
                  </tr>
                ) : (
                  filtered.map((ev, i) => (
                    <tr key={ev.id || i}>
                      <td className="muted mono" data-label={t('usageEvents.column.timestamp')} style={{ fontSize: 11 }}>
                        {ev.eventTimeStamp ? new Date(ev.eventTimeStamp).toLocaleString('en-GB') : '—'}
                      </td>
                      <td className="id" data-label={t('usageEvents.column.account')}>{ev.accountId || '—'}</td>
                      <td data-label={t('usageEvents.column.event')}>
                        <span className={'pill pill--' + (ev.eventType?.includes('IN') ? 'ok' : 'neutral')}>
                          <span className="pill-dot" />
                          {(ev.eventType || '—').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="muted" data-label={t('usageEvents.column.location')}>{ev.eventLocationIdentifier || '—'}</td>
                      <td className="muted" data-label={t('usageEvents.column.supplier')}>
                        {ev.eventSourceSupplierName || ev.eventSourceSupplierRics || '—'}
                      </td>
                      <td className="id" data-label={t('usageEvents.column.agreement')}>{ev.agreementId || '—'}</td>
                      <td className="id" data-label={t('usageEvents.column.transaction')}>{ev.transactionId || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  )
}
